import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircleIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { languageOptions } from '@constants/languages.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import {
  updateCompanySchema,
  type UpdateCompanyDto,
  type UpdateCompanyPayload,
} from '@services/companies/dtos/update.dto';
import { companyService } from '@services/companies/service';
import type { GetCompanyByOwnerIdOutput } from '@services/companies/types/getByOwnerId.type';
import type { UpdateCompanyOutput } from '@services/companies/types/update.type';

function companyQueryOptions(providerId: string) {
  return queryOptions({
    queryKey: queryKeys.companies.detail(providerId),
    queryFn: () => companyService.getByOwnerId(providerId),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/providers/$id/companies/$companyId/update',
)({
  component: UpdateCompany,
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData({
      ...companyQueryOptions(params.id),
      revalidateIfStale: true,
    }),
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircleIcon className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMs: 300,
  pendingMinMs: 600,
});

function UpdateCompany() {
  const { queryClient } = Route.useRouteContext();
  const { companyId, id } = Route.useParams();
  const navigate = Route.useNavigate();

  const {
    data: { company },
  } = useSuspenseQuery(companyQueryOptions(id));

  const form = useForm<UpdateCompanyDto>({
    resolver: zodResolver(updateCompanySchema),
    mode: 'onBlur',
    defaultValues: {
      email: company.email,
      language: company.language,
      name: company.name,
      phone: company.phone,
    },
  });

  const { isPending, mutate: updateCompany } = useMutation<
    UpdateCompanyOutput,
    unknown,
    UpdateCompanyPayload
  >({
    mutationFn: companyService.update,
    onError: handleRequestError,
    onSuccess: async ({ company: updatedCompany }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.companies.detail(id),
      });

      queryClient.setQueryData<GetCompanyByOwnerIdOutput>(
        queryKeys.companies.detail(id),
        oldData => {
          if (!oldData) {
            return oldData;
          }

          return { company: updatedCompany };
        },
      );

      navigate({ to: '/providers/$id', params: { id }, replace: true });
      toast.success('Success', {
        description: 'Company updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateCompany({
      id: companyId,
      email: values.email,
      language: values.language,
      name: values.name,
      phone: values.phone,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id }}
        title="Update Company"
        to="/providers/$id"
      />

      <form
        className="mx-auto mt-5 space-y-3 p-2 md:w-4/5 2xl:w-3/5"
        onSubmit={handleSubmit}
      >
        <div className="grid h-fit grid-cols-3 items-start gap-2">
          <InputControlled label="Name" name="name" />
          <InputControlled label="Email" name="email" type="email" />
          <InputControlled label="Phone" name="phone" variant="phone" />
          <InputControlled
            label="Language"
            name="language"
            options={languageOptions}
            variant="select"
          />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{ id }}
            rounded="lg"
            size="lg"
            to="/providers/$id"
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
